/**
 * AdContext — page-local ad de-duplication.
 *
 * Each page can render 3-5 `<AdDisplay>` slots (header, banner, sidebar,
 * footer, etc.). Because every `<AdDisplay>` fires its own weighted-
 * random request to `/api/ads/{slot}`, the same creative could win 2-3
 * slots on the same page — especially when a pool has only 1-2 rotating
 * creatives. That looks like a bug to the visitor.
 *
 * This context solves it two ways:
 *   1. Fetches are serialized through a single promise chain so each
 *      request happens AFTER the previous one resolved.
 *   2. Every winning `campaign_id` is added to a "shown" set, and the
 *      set is sent as `?exclude=…` on the next request so the backend
 *      won't pick the same creative twice.
 *
 * The set resets whenever the route changes (via `useLocation`) so a
 * fresh page load gets a fresh rotation.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../App';

export const AdContext = createContext({ fetchAd: async () => [] });

export const AdProvider = ({ children }) => {
  const { API } = useContext(AuthContext);
  const shownRef = useRef(new Set());
  const queueRef = useRef(Promise.resolve());
  const routeLocation = useLocation();

  // Fresh page = fresh rotation. Reset the "already shown" set every
  // time the pathname changes so returning to a page gives a new mix.
  useEffect(() => {
    shownRef.current = new Set();
    queueRef.current = Promise.resolve();
  }, [routeLocation.pathname]);

  const fetchAd = useCallback((slot) => {
    // Chain each fetch after the previous so `shownRef` has the winning
    // IDs from earlier slots before we make the next call. Errors on
    // one slot must not poison the chain for the next slot.
    const next = queueRef.current.then(async () => {
      try {
        const exclude = Array.from(shownRef.current).join(',');
        const q = exclude ? `?exclude=${encodeURIComponent(exclude)}` : '';
        const r = await axios.get(`${API}/ads/${slot}${q}`);
        const ads = Array.isArray(r.data) ? r.data : [];
        ads.forEach((a) => {
          if (a?.campaign_id) shownRef.current.add(a.campaign_id);
        });
        return ads;
      } catch (_) {
        return [];
      }
    });
    queueRef.current = next.catch(() => {});
    return next;
  }, [API]);

  return <AdContext.Provider value={{ fetchAd }}>{children}</AdContext.Provider>;
};

export default AdProvider;
